import React from 'react';
import { Quote, User, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { testimonials } from '../data/mock';

const TestimonialsPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 medical-pattern opacity-50" />
        <div className="decorative-circle w-96 h-96 -top-48 -right-48 opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full text-teal-700 text-sm font-medium mb-6">
              <MessageCircle className="w-4 h-4" />
              <span>Retours d'expérience</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-light text-gray-900">
              Témoignages patients
            </h1>
            <p className="mt-6 text-xl text-gray-600 leading-relaxed">
              Retours d'expérience de patients pris en charge au cabinet. Ces témoignages reflètent des parcours individuels et ne constituent pas des promesses de résultats.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={testimonial.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="h-1 bg-gradient-to-r from-teal-400 to-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center flex-shrink-0">
                      <Quote className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="pt-1">
                      <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                        {testimonial.context}
                      </span>
                    </div>
                  </div>
                  
                  <blockquote className="text-gray-700 leading-relaxed text-lg mb-6 italic">
                    "{testimonial.text}"
                  </blockquote>
                  
                  <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {testimonial.initials}
                      </p>
                      <p className="text-sm text-gray-500">
                        Patient
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Note */}
      <section className="py-16 bg-gray-50 medical-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border-0 shadow-lg bg-white overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-gray-300 to-gray-400" />
              <CardContent className="p-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">i</span>
                  </span>
                  À propos de ces témoignages
                </h3>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Les témoignages présentés sur cette page sont issus de retours spontanés de patients et ont été recueillis avec leur accord.
                  </p>
                  <p>
                    Ils reflètent des expériences individuelles et ne préjugent pas des résultats pour d'autres patients. Chaque situation médicale est unique.
                  </p>
                  <p className="text-sm text-gray-500 italic pt-2 border-t border-gray-100">
                    Conformément aux recommandations de l'Ordre des Médecins, ces témoignages sont présentés de manière factuelle, sans exagération ni promesse de résultat.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TestimonialsPage;
